/**
 * @lodex-pro/web-shared/shell/TenantBrand — the company's logo or name in the
 * sidebar header (quick 261013-srg PR 0).
 *
 * Replaces the old "lodeX Pro / Hauler Tenant" header in web-dispatch and
 * web-billing. The product mark ("lodeX Pro") moves to the sidebar footer
 * (AppShell), per the branding split: the product UI still says lodeX Pro.
 *
 * Look (Corbin, 2026-10-14 — picked option "B, fall back to A if colour" from a
 * mockup, after the first version showed a squashed 40px logo next to the name):
 *   - one-colour logo → drawn in white, full width, straight on the dark menu;
 *   - colour logo     → full width on a white rounded card;
 *   - no logo         → initials badge + the name, wrapping to two lines.
 * A logo usually contains the company name, so the name is not repeated beside it;
 * it is the image's alt text and the hover tooltip.
 *
 * Data:
 *   - Name: GET /v1/users/me/tenants → the ACTIVE membership's tenantName.
 *   - Logo: GET /v1/tenants/branding → { logo: { dataUrl, monochrome } | null }, the
 *     company logo (task #58: Settings → Company logo; the thermal ticket logo is never
 *     shown here). The server already turned a one-colour logo white and trimmed it.
 *     Settings → Company logo invalidates TENANT_BRAND_LOGO_KEY on upload and delete.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { authedFetch } from '../auth/api-client.js';
import { useApiOpts } from '../api/api-opts-context.js';
import type { TenantMembership } from '../auth/tenant-picker.js';

/** TanStack Query key for the active tenant's logo. Invalidate on logo upload/delete. */
export const TENANT_BRAND_LOGO_KEY = ['branding', 'logo'] as const;
/** TanStack Query key for the active tenant's display name. */
export const TENANT_BRAND_NAME_KEY = ['branding', 'name'] as const;

/** Logo and name rarely change; refetch at most every 30 minutes while signed in. */
const BRAND_STALE_MS = 30 * 60 * 1000;
/**
 * Drop the cached name and logo as soon as the shell unmounts. Sign-out (and a
 * tenant switch, which signs out) unmounts the shell but does NOT clear the
 * QueryClient, so a longer gcTime would show the previous company to the next
 * person who signs in on the same tab.
 */
const BRAND_GC_MS = 0;

/** Up to two uppercase initials from a company name ("Sooner State Trucking" → "SS"). */
export function tenantInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word.replace(/[^A-Za-z0-9]/g, ''))
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/** The company logo as the sidebar shows it. */
export interface TenantLogo {
  /** A data: URL for the PNG. */
  readonly src: string;
  /** True: white-on-transparent, drawn on the dark menu. False: colour, on a white card. */
  readonly monochrome: boolean;
}

interface MeTenantsResponse {
  readonly memberships: ReadonlyArray<TenantMembership>;
  readonly activeTenantId: string | null;
}

interface BrandingResponse {
  readonly logo: { readonly dataUrl: string; readonly monochrome: boolean } | null;
}

export interface TenantBrandData {
  /** The active company's name; null while loading or if it cannot be resolved. */
  readonly tenantName: string | null;
  /** The logo; null when there is none (show initials + name). */
  readonly logo: TenantLogo | null;
}

/** Load the active company's name and logo for the sidebar header. */
export function useTenantBrand(): TenantBrandData {
  const opts = useApiOpts();

  const name = useQuery<string | null>({
    queryKey: TENANT_BRAND_NAME_KEY,
    staleTime: BRAND_STALE_MS,
    gcTime: BRAND_GC_MS,
    queryFn: async (): Promise<string | null> => {
      const res = await authedFetch(opts, '/v1/users/me/tenants');
      if (!res.ok) throw new Error(`tenant name: HTTP ${String(res.status)}`);
      const body = (await res.json()) as MeTenantsResponse;
      const active = body.memberships.find((m) => m.tenantId === body.activeTenantId);
      return active?.tenantName ?? null;
    },
  });

  const logo = useQuery<TenantLogo | null>({
    queryKey: TENANT_BRAND_LOGO_KEY,
    staleTime: BRAND_STALE_MS,
    gcTime: BRAND_GC_MS,
    queryFn: async (): Promise<TenantLogo | null> => {
      const res = await authedFetch(opts, '/v1/tenants/branding');
      if (!res.ok) throw new Error(`tenant logo: HTTP ${String(res.status)}`);
      const body = (await res.json()) as BrandingResponse;
      if (body.logo === null) return null;
      return { src: body.logo.dataUrl, monochrome: body.logo.monochrome };
    },
  });

  return { tenantName: name.data ?? null, logo: logo.data ?? null };
}

export interface TenantBrandProps {
  /** The company's name; null while it is loading. */
  readonly tenantName: string | null;
  /** The company's logo; null/undefined shows initials + name. */
  readonly logo?: TenantLogo | null;
}

const MARK_PX = 40;
/** Tallest the logo may be, so a square or tall logo does not push the menu down. */
const LOGO_MAX_HEIGHT_PX = 64;
/** A long company name wraps to this many lines, then ends in an ellipsis. */
const NAME_MAX_LINES = 2;

/** Company logo, or initials and name, for the top of the sidebar. */
export function TenantBrand({ tenantName, logo }: TenantBrandProps): React.JSX.Element {
  // A broken image (bad bytes) falls back to initials rather than a broken-image icon.
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => {
    setImgFailed(false);
  }, [logo?.src]);

  const name = tenantName ?? '';

  if (logo !== null && logo !== undefined && !imgFailed) {
    const img = (
      <img
        src={logo.src}
        alt={tenantName ?? 'Company logo'}
        onError={(): void => setImgFailed(true)}
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: `${String(LOGO_MAX_HEIGHT_PX)}px`,
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
        }}
      />
    );
    return (
      <div
        data-testid="tenant-brand"
        data-logo-style={logo.monochrome ? 'mono' : 'card'}
        title={tenantName ?? undefined}
        style={
          logo.monochrome
            ? { display: 'flex', alignItems: 'center', minWidth: 0 }
            : {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
                padding: '10px 12px',
                borderRadius: '8px',
                // The card is white in both themes on purpose: it is the logo's own
                // background, so a colour logo looks the way the company drew it.
                backgroundColor: '#ffffff',
              }
        }
      >
        {img}
      </div>
    );
  }

  return (
    <div
      data-testid="tenant-brand"
      style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}
    >
      <div
        aria-hidden="true"
        data-testid="tenant-brand-initials"
        style={{
          width: `${String(MARK_PX)}px`,
          height: `${String(MARK_PX)}px`,
          borderRadius: '8px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-bg-tertiary)',
          border: '1px solid var(--color-border-secondary)',
          color: 'var(--color-text-primary)',
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        {tenantInitials(name)}
      </div>
      {/* A long name wraps onto a second line and ends in "…" after that (Corbin,
          2026-10-14 — one line cut "SOONER STATE TRUCKING" short). Full name on hover. */}
      <div
        title={tenantName ?? undefined}
        data-testid="tenant-brand-name"
        style={{
          minWidth: 0,
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: 1.2,
          color: 'var(--color-text-primary)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: NAME_MAX_LINES,
          overflowWrap: 'anywhere',
        }}
      >
        {name}
      </div>
    </div>
  );
}
